import type { CartLine, BundleLine } from '@/stores/cart.store';
import type {
  BillingInfo,
  CreateSalePayload,
  OrderBundleItem,
  OrderItem,
  PaymentGateway,
  PromoGroupType,
  SaleIdentity,
  SourceChannel,
} from '@/types/sale';
import type { CardReader } from '@/types/sale';

interface BuildArgs {
  lines: CartLine[];
  bundleLines?: BundleLine[];
  customer: { name: string; phone: string; memberId?: string };
  visitDate: string;
  couponCode: string | null;
  discountIds: string[];
  paymentMethodId: string;
  paymentGateway: PaymentGateway;
  /** cash only. */
  amountTendered?: number;
  /** e-money only. */
  reader?: CardReader;
  /** v.1.0.22 unified promo-scheme context — all optional. */
  groupType?: PromoGroupType | null;
  sourceChannel?: SourceChannel | null;
  identity?: SaleIdentity | null;
  /** v.1.0.24 — single-use token from POST /document/scan; gates OCR promos. */
  documentScanToken?: string | null;
}

/**
 * Build the STRICT POST /sale payload. billingInfo is a strict server-side
 * object — we send ONLY the keys the chosen gateway needs. For cash we send
 * amountTendered only (never client-side changeDue). For e-money we send the
 * reader. For midtrans neither.
 */
export function buildSalePayload(args: BuildArgs): CreateSalePayload {
  // Expand each line's per-seat config into BE items. Seats WITH add-ons emit
  // one quantity-1 item each (so add-on quantities map unambiguously onto the
  // item shape); seats WITHOUT add-ons are grouped by slotId into a single
  // quantity-N item carrying the group's non-blank visitor labels.
  const items: OrderItem[] = [];
  for (const line of args.lines) {
    // Seats WITH add-ons → one quantity-1 item each (add-on quantities map 1:1).
    for (const seat of line.seats) {
      if (seat.addOns.length === 0) continue;
      const item: OrderItem = {
        productId: line.productId,
        quantity: 1,
        addOnItems: seat.addOns.map((a) => ({
          productId: a.productId,
          quantity: a.quantity,
          ...(a.slotId ? { slotId: a.slotId } : {}),
        })),
      };
      if (seat.slotId) item.slotId = seat.slotId;
      const label = seat.label?.trim();
      if (label) item.unitLabels = [label];
      // Attach the applied Smart Itinerary so it rides onto the wristband.
      if (seat.itinerary?.configured) item.itinerary = seat.itinerary;
      items.push(item);
    }

    // Seats WITHOUT add-ons → grouped by slotId into one quantity-N item.
    const bySlot = new Map<string, typeof line.seats>();
    for (const seat of line.seats) {
      if (seat.addOns.length > 0) continue;
      const k = seat.slotId ?? '';
      const bucket = bySlot.get(k);
      if (bucket) bucket.push(seat);
      else bySlot.set(k, [seat]);
    }
    for (const [slotId, seats] of bySlot) {
      const item: OrderItem = {
        productId: line.productId,
        quantity: seats.length,
        addOnItems: [],
      };
      if (slotId) item.slotId = slotId;
      // The BE requires unitLabels.length === quantity, so only send them when
      // EVERY seat in the group is named; otherwise let the BE default all N.
      const labels = seats.map((s) => s.label?.trim() ?? '');
      if (labels.every((l) => l.length > 0)) item.unitLabels = labels;
      items.push(item);
    }
  }

  // Bundles: send bundleId + quantity (server re-resolves price/type/components).
  // FLEXIBLE bundles also carry the customer's item picks. Every slot-required
  // component carries its picked slot in componentSlots — the BE validator
  // rejects the sale unless each requiresTimeSlot component has one (aggregate
  // form: all units of the component share the chosen slot).
  const bundleItems: OrderBundleItem[] = (args.bundleLines ?? []).map((b) => {
    // addOnItems carries EXTRA attractions the cashier attached on top of the
    // bundle's fixed components (required by the BE schema — empty when none).
    // bundleQuantity:0 tells the BE the bundle covers none of these for free,
    // so every extra unit is charged at its own rate.
    const addOnItems = (b.extraAddOns ?? []).map((a) => ({
      productId: a.productId,
      quantity: a.quantity,
      bundleQuantity: 0,
      ...(a.slotId ? { slotId: a.slotId } : {}),
    }));
    const item: OrderBundleItem = { bundleId: b.bundleId, quantity: b.quantity, addOnItems };
    if (b.selectedItems && b.selectedItems.length > 0) item.selectedItems = b.selectedItems;
    const componentSlots = (b.components ?? [])
      .filter((c) => c.slotId)
      .map((c) => ({ productId: c.productId, slotId: c.slotId as string }));
    if (componentSlots.length > 0) item.componentSlots = componentSlots;
    return item;
  });

  const billingInfo: BillingInfo = {
    paymentMethodId: args.paymentMethodId,
    paymentGateway: args.paymentGateway,
  };
  if (args.paymentGateway === 'cash' && typeof args.amountTendered === 'number') {
    billingInfo.amountTendered = args.amountTendered;
  }
  if (args.paymentGateway === 'e-money' && args.reader) {
    billingInfo.reader = { mid: args.reader.mid, tid: args.reader.tid };
  }

  const payload: CreateSalePayload = {
    userInfo: {
      name: args.customer.name.trim(),
      ...(args.customer.phone?.trim() ? { phone: args.customer.phone.trim() } : {}),
      ...(args.customer.memberId ? { memberId: args.customer.memberId } : {}),
    },
    orderDetails: {
      items,
      bundleItems,
      visitDate: args.visitDate,
    },
    billingInfo,
  };
  if (args.couponCode) payload.couponCode = args.couponCode;
  if (args.discountIds.length > 0) payload.discountIds = args.discountIds;

  // Promo-scheme context — only send keys the cashier actually set, so a
  // plain sale stays a plain sale (the BE gates purely on presence).
  if (args.groupType) payload.groupType = args.groupType;
  if (args.sourceChannel) payload.sourceChannel = args.sourceChannel;

  const identity = cleanIdentity(args.identity);
  if (identity) payload.identity = identity;

  if (args.documentScanToken) payload.documentScanToken = args.documentScanToken;

  return payload;
}

/**
 * Drop empty/blank fields from a KTP attestation, returning null when nothing
 * meaningful was entered — so we never send an all-null identity object that
 * would trip the BE's fail-closed identity gate for a non-identity sale.
 */
function cleanIdentity(identity?: SaleIdentity | null): SaleIdentity | null {
  if (!identity) return null;
  const region = identity.idCardRegion?.trim() || null;
  const number = identity.idCardNumber?.trim() || null;
  const birthMonth = identity.birthMonth ?? null;
  const ktpVerified = identity.ktpVerified === true;
  if (!region && !number && birthMonth == null && !ktpVerified) return null;
  const out: SaleIdentity = {};
  if (region) out.idCardRegion = region;
  if (number) out.idCardNumber = number;
  if (birthMonth != null) out.birthMonth = birthMonth;
  if (ktpVerified) out.ktpVerified = true;
  return out;
}
